"use client"

import {
  addArticleToCurrentBasketAction,
  determineCrossSellingProductsAction,
  fetchExtendedPricesAction,
  fetchProductAction,
} from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"
import { ShoppingCart } from "lucide-react"

import { logger } from "@/lib/logger"
import { LegacyProductPriceContainer } from "@/lib/storefront/product/legacy-price-service"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { trackAddToBasket, trackFFProductClick } from "@/lib/tracking/tracking-helper"
import { Button } from "@/components/ui/components"

export default function AddToBasketButtonClient(props: {
  isoLocale: string
  isB2BCustomer: boolean
  articleIdToAdd: string
  productData: ReducedProductData | undefined
  prices: ProductPriceContainerCtnrROV1 | undefined
  quantity: number
  label?: string
  className?: string
  showSmall?: boolean
  position?: number
  trackingList?: string
  searchTerm?: string | undefined
  categoryPath?: string[]
  pagetyp?: string
  campaignId?: string
  simulateProductClick: boolean
  text?: string
}) {
  const {
    setAddToBasketDialogVisible,
    setProductDataAddedToBasket,
    setBasketData,
    setCrosssellingProductDataAddedToBasket,
    setProductDataAddedToBasketPrices,
    setProductDataAddedToBasketCustomerCategoryDiscount,
    setIsLoading,
    userData,
  } = useUbStorefrontState()
  // const t = useTranslations("storefront")

  const addToCartPagetyp =
    props.pagetyp ??
    (props.trackingList === "search"
      ? "search"
      : props.trackingList === "category" && props.categoryPath?.length
        ? `category_l${Math.min(props.categoryPath.length, 4)}`
        : "pdp_artikel")
  const addToCartCategoryPath = props.categoryPath?.filter(Boolean).join("/")

  async function addToBasket() {
    if (!props.articleIdToAdd) {
      return false
    }

    if (props.simulateProductClick) {
      trackFFProductClick(
        props.productData,
        props.trackingList ?? "direct",
        props.position ?? 1,
        props.searchTerm,
        props.productData?.ffCampaignId || props.campaignId
      )
    }

    try {
      const res = await addArticleToCurrentBasketAction(props.articleIdToAdd, props.quantity)
      // console.log(`ProductCardAddToBasket: props.quantity=${props.quantity}`)
      // console.log(`ProductCardAddToBasket: setBasketData()`)
      // console.dir(newBasketData)
      setBasketData(res.basketData)
      setProductDataAddedToBasketCustomerCategoryDiscount(res.customerCategoryDiscount)
      if (res.result === "NOK") {
        logger.error(`ProductCardAddToBasket: Backend problem: ${res.message}`)
        return false
      }

      trackAddToBasket(res.isoLocale, res.isB2B, res.product, props.prices ?? res.prices, props.quantity, {
        isAnonymous: !userData,
        pagetyp: addToCartPagetyp,
        categoryPath: addToCartCategoryPath,
      })

      determineCrossSellingProductsAction(props.articleIdToAdd)
        .then(setCrosssellingProductDataAddedToBasket)
        .catch((reason) => {
          logger.error(`ProductCardAddToBasket: Could not load cross-selling products: ${reason}`)
        })

      return true
    } catch (reason) {
      logger.error(`ProductCardAddToBasket: Backend problem: ${reason}`)
      return false
    }
  }

  async function addToBasketAndShowDialog() {
    setIsLoading(true)
    try {
      const basketWasUpdated = await addToBasket()
      if (basketWasUpdated) {
        // console.log(`ProductCardAddToBasket: setProductDataAddedToBasket`)
        // console.dir(props.productData)
        if (props.productData) {
          setProductDataAddedToBasket(props.productData)
        } else {
          fetchProductAction(props.articleIdToAdd).then((product: ReducedProductData | undefined) => {
            setProductDataAddedToBasket(product)
          })
        }
        // console.log(`ProductCardAddToBasket: setProductDataAddedToBasketPrices`)
        // console.dir(props.prices)
        if (props.prices) {
          setProductDataAddedToBasketPrices(props.prices)
        } else {
          fetchExtendedPricesAction(props.articleIdToAdd).then((prices: LegacyProductPriceContainer | undefined) => {
            setProductDataAddedToBasketPrices(prices?.productPriceContainer)
          })
        }
        setAddToBasketDialogVisible(true)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button
      data-skip-product-click-tracking
      className={props.className}
      aria-label={props.label}
      size={props.showSmall ? "productCardIconSmall" : "productCardIcon"}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        addToBasketAndShowDialog()
      }}
    >
      {props.label}
      <div className={"flex flex-row items-center justify-between gap-2"}>
        <ShoppingCart className={"size-4"} />
        {props.text && <p className={"text-xs"}>{props.text}</p>}
      </div>
    </Button>
  )
}
